import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Button,
	ChakraProvider,
	Flex,
	Icon,
	IconButton,
	Input,
	Popover,
	PopoverArrow,
	PopoverCloseButton,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
	Stack,
	Text,
	Textarea,
	useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { scrollBarStyle } from '../../utils/layoutConfig';
import { UilTrashAlt, UilEdit, UilPlus, UilClipboard } from '@iconscout/react-unicons';
import { JsonFormatter } from 'react-json-formatter';
//uuidv4
import { v4 as uuidv4 } from 'uuid';
import { types } from '../canvas/SelectableCssVariables/SelectableCssVariablesDropdown';
import { assignNewVariables } from '../../utils/cssGlobalVariables';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import components from '../../utils/componentsDictionary';
import VtexJsonUploadComponent from './VtexJsonUploadComponent';
import { IntlProvider } from 'react-intl';
import theme from '../../utils/ChakraTheme';
import { ChromePicker } from 'react-color';
import '../../templates/vtex-apps/mz-templates.scss';
import _ from 'lodash';
import { getAuthHeader } from '../../utils/auth-token';

/**
 * File
 * {
 * name: string,
 * type: string,
 * content: string
 * isJsonVariable: boolean
 * isFolder: boolean
 * }
 */

export function formatJson(text, setJsonError, jsonError) {
	try {
		const regexRemoveComments = /\/\*[\s\S]*?\*\/|\/\/(?!(?:\s*(?:https?:)?\/\/))/gm;
		const json = JSON.parse(text.replace(regexRemoveComments, ''));
		jsonError && setJsonError(null);
		return JSON.stringify(json, null, 4);
	} catch (e) {
		setJsonError && !jsonError && setJsonError('Mano, esse seu JSON não tá legal não ' + e.message);
		return text;
	}
}
const draggingStyle = (dragging) => ({
	border: ` ${dragging ? 'dashed 2px #ecc94b' : '1px solid black'}`,
	backgroundColor: dragging ? '#fffee6' : 'transparent',
});

const VtexJsonToHefesto = () => {
	const [textData, setTextData] = React.useState('');
	const [files, setFiles] = React.useState([]);
	const [dragging, setDragging] = React.useState(false);
	const [selectedFile, setSelectedFile] = React.useState(null);
	const [jsonError, setJsonError] = React.useState(false);
	const [hefestoJson, setHefestoJson] = React.useState(null);
	const [compiledCss, setCompiledCss] = React.useState(null);
	const [defaultVariables, setDefaultVariables] = React.useState(assignNewVariables());
	const [variablesList, setVariablesList] = React.useState([
		{
			type: 'color',
			name: 'a',
			value: '--support-color-1',
		},
	]);
	const textareaRef = React.useRef(null);
	const scssTextareaRef = React.useRef(null);

	const handleDragOver = (event) => {
		event.preventDefault();
		setDragging(true);
	};

	const handleDragLeave = () => {
		setDragging(false);
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setDragging(false);
		const filesToUpload = event.dataTransfer.files;
		const fileReaders = [];
		for (let i = 0; i < filesToUpload.length; i++) {
			const file = filesToUpload[i];
			const extension = file.name.split('.').pop();
			if (['json', 'jsonc', 'scss'].includes(extension)) {
				const reader = new FileReader();
				fileReaders.push(
					new Promise((resolve) => {
						reader.onload = () => {
							const content = reader.result;
							resolve({
								id: uuidv4(),
								name: file.name,
								type: extension,
								content: content,
								isJsonVariable: extension === 'json' ? true : false,
								isFolder: false,
							});
						};
					}),
				);
				reader.readAsText(file);
			} else {
				console.log(`Invalid file type: ${file.name}`);
			}
		}
		Promise.all(fileReaders).then((results) => {
			setFiles([...files, ...results]);
		});
	};

	function getFileIcon(type) {
		return <UilEdit />;
		if (!type) return 'default';
		switch (type) {
			case 'json':
				return 'json';
			case 'scss':
				return 'scss';
			case 'folder':
				return 'folder';
		}
	}

	function tryToCompile() {
		const jsonFiles = files.filter((file) => file.type === 'jsonc');
		if (jsonFiles.length === 0) return;

		const mergedJson = jsonFiles.reduce((acc, curr) => {
			curr.content = curr.content.replace(/\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm, '');
			return Object.assign(acc, JSON.parse(curr.content));
		}, {});
		//recursion to move all childrens to the respective parents based on the key/children property
		const keys = Object.keys(mergedJson);
		const orphans = [];
		const newJson = {};
		const jsonComponentsCache = {};
		//adding components to the cache
		keys.forEach((key) => {
			const componentFromDict = components.find((c) => c.name === key.split('#')[0]);
			if (componentFromDict) {
				jsonComponentsCache[key] = componentFromDict.componentProps(
					mergedJson[key]?.props,
					mergedJson[key]?.children,
					mergedJson[key]?.blocks,
				);
				newJson[key] = JSON.parse(JSON.stringify(jsonComponentsCache[key]));
			}
		});

		function moveChildrensToParents(newJson, keys, newJsonOriginal) {
			keys.forEach((key) => {
				//the component
				if (newJson[key]?.childrenComponents?.length) {
					//find the object that has the key as the name and move it to the children array
					const childrens = newJson[key].childrenComponents;
					childrens.forEach((child) => {
						if (jsonComponentsCache[child]) {
							const comp = jsonComponentsCache[child];
							newJson[key].childrenComponents = newJson[key].childrenComponents.filter((c) => c !== child);
							newJson[key].childrenComponents.push(comp);
							moveChildrensToParents({ comp }, ['comp'], newJsonOriginal);
							delete newJsonOriginal[child];
						} else {
							orphans.push(child);
						}
					});
				}
				//the same thing for blocks
				if (newJson[key]?.blocks?.length) {
					const blocks = newJson[key].blocks;
					blocks.forEach((block) => {
						if (jsonComponentsCache[block]) {
							const comp = jsonComponentsCache[block];
							newJson[key].blocks = newJson[key].blocks.filter((c) => c !== block);
							newJson[key].blocks = [...newJson[key].blocks, jsonComponentsCache[block]];
							moveChildrensToParents({ comp }, ['comp'], newJsonOriginal);
							delete newJsonOriginal[block];
						} else {
							orphans.push(block);
						}
					});
				}
				if (Object.keys(newJson[key]?.props || {}).length) {
					Object.keys(newJson[key].props).forEach((prop) => {
						//if prop start with upperCase, it's a component
						if (prop[0] === prop[0].toUpperCase()) {
							const propValue = newJson[key].props[prop];
							if (jsonComponentsCache[newJson[key].props[prop]]) {
								const comp = jsonComponentsCache[newJson[key].props[prop]];
								newJson[key].props[prop] = jsonComponentsCache[newJson[key].props[prop]];
								moveChildrensToParents({ comp }, ['comp'], newJsonOriginal);
								delete newJsonOriginal[propValue];
							} else {
								orphans.push(newJson[key].props[prop]);
							}
						}
					});
				}
			});
		}
		moveChildrensToParents(newJson, keys, newJson);
		setHefestoJson(newJson[Object.keys(newJson)[0]]);

		//filter files by type to get all scss
		let scss = files
			.filter((file) => file.type === 'scss')
			.map((file) => file.content)
			.join(' ');
		scss = `.canvas{${scss}}`;
		//fetch post to /compileTemplateCSS
		if (scss) {
			fetch(`${process.env.REACT_APP_COMPILER_URL}/api/tachyons/compileTemplateCSS/`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...getAuthHeader(),
				},
				body: JSON.stringify({
					scss,
				}),
			})
				.then((resp) => resp.text())
				.then((resp) => {
					setCompiledCss(resp);
				})
				.catch((e) => console.log(e));
		}
	}

	function removeFile(e, index) {
		e.stopPropagation();
		const newFiles = [...files];
		const fileToBeRemoved = newFiles[index];
		if (fileToBeRemoved.id === selectedFile?.id) {
			setSelectedFile(null);
		}
		newFiles.splice(index, 1);
		setFiles(newFiles);
	}

	function selectFile(index) {
		return () => {
			setSelectedFile(files[index]);
		};
	}

	// function findCustomVariablesFiles() {
	//     let customVariablesFiles = files.filter(file => file.isJsonVariable)
	//     return customVariablesFiles?.[0] || null
	// }

	function updateFile(e) {
		const newFiles = [...files];
		const fileToBeUpdated = newFiles.find((file) => file.id === selectedFile.id);
		fileToBeUpdated.content = e.target.value;
		setFiles(newFiles);
		setSelectedFile(fileToBeUpdated);
	}

	return (
		<>
			<ChakraProvider theme={theme} resetCSS={false}>
				<Flex flexWrap={'wrap'}>
					{defaultVariables && (
						<style>{`.canvas,.canvas-iframe{${Object.keys(defaultVariables)
							.map((key) => `${key}:${defaultVariables[key]}`)
							.join(';')}}`}</style>
					)}
					{variablesList?.length && (
						<style>{`.canvas,.canvas-iframe{
                            ${variablesList.map((variable) => `--${variable.name}:var(${variable.value})`).join(';')}
                        }`}</style>
					)}
					<JsonToHefestoHeader tryToCompile={tryToCompile} />
					<Flex
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						style={draggingStyle(dragging)}
						maxH={'400px'}
						overflow={'auto'}
						sx={scrollBarStyle}
						className="file-manager"
						flexDir={'column'}
						p={4}
						w={'18%'}
					>
						<Text>Arraste e solte os arquivos aqui</Text>
						<Stack my={4}>
							{files?.map((file, index) => {
								const isSelected = selectedFile?.id === file.id;
								return (
									<Box
										key={file.id}
										px={4}
										background={isSelected ? '#ecc94b' : 'transparent'}
										_hover={{ background: '#cccccc', cursor: 'pointer' }}
										display={'flex'}
										alignItems={'center'}
										onClick={selectFile(index)}
									>
										<Icon icon={getFileIcon(file.type)} />
										<Text pl={2}>{file.name}</Text>
										<IconButton
											size="xs"
											icon={<UilTrashAlt />}
											ml={'auto'}
											onClick={(e) => {
												removeFile(e, index);
											}}
										/>
									</Box>
								);
							})}
						</Stack>
					</Flex>
					<Flex border={'1px solid black'} className="file-preview" flexDir={'column'} p={4} w={'40%'}>
						{selectedFile && (
							<>
								<Text>{selectedFile.name}</Text>
								<Box maxH={'400px'} h={'100%'}>
									<Box my={4} fontFamily={'Consolas'} fontSize={'sm'} overflow={'auto'} h={'300px'} bg={'#1e1e1e'}>
										{selectedFile.type.includes('json') ? (
											<Box
												h={'auto'}
												position={'relative'}
												display={'flex'}
												role="button"
												tabIndex={0}
												onKeyDown={() => textareaRef.current?.focus()}
												onClick={() => textareaRef.current?.focus()}
											>
												<Textarea
													h={'100%'}
													minH={'285px'}
													spellCheck="false"
													ref={textareaRef}
													p={1}
													sx={{
														resize: 'none',
														border: 0,
														overflow: 'hidden',
														caretColor: 'white',
													}}
													lineHeight={'1.5'}
													fontSize={'12px'}
													position={'absolute'}
													inset={0}
													background={'transparent'}
													fontFamily={'SFMono-Regular,Menlo,Monaco,Consolas,monospace'}
													w={'100%'}
													h={'100%'}
													color={'transparent'}
													overflow={'auto'}
													value={formatJson(selectedFile.content, setJsonError, jsonError)}
													onChange={(e) => updateFile(e)}
												/>
												<SyntaxHighlighter
													wrapLongLines={true}
													style={atomOneDark}
													language="json"
													customStyle={{
														margin: 0,
														flex: '1',
														width: '100%',
														background: 'transparent',
														fontSize: '12px',
														overflow: 'initial',
													}}
												>
													{formatJson(selectedFile.content, setJsonError, jsonError)}
												</SyntaxHighlighter>
											</Box>
										) : (
											<Box
												h={'auto'}
												position={'relative'}
												display={'flex'}
												role="button"
												tabIndex={0}
												onKeyDown={() => scssTextareaRef.current?.focus()}
												onClick={() => scssTextareaRef.current?.focus()}
											>
												<Textarea
													h={'100%'}
													minH={'285px'}
													spellCheck="false"
													ref={scssTextareaRef}
													p={1}
													sx={{ overflow: 'hidden', caretColor: 'white' }}
													lineHeight={'1.5'}
													fontSize={'12px'}
													position={'absolute'}
													inset={0}
													background={'transparent'}
													fontFamily={'SFMono-Regular,Menlo,Monaco,Consolas,monospace'}
													w={'100%'}
													h={'100%'}
													color={'transparent'}
													overflow={'auto'}
													value={selectedFile.content}
													onChange={(e) => updateFile(e)}
												/>
												<SyntaxHighlighter
													wrapLongLines={true}
													style={atomOneDark}
													language="scss"
													customStyle={{
														margin: 0,
														flex: '1',
														width: '100%',
														background: 'transparent',
														fontSize: '12px',
														overflow: 'initial',
													}}
												>
													{selectedFile.content}
												</SyntaxHighlighter>
											</Box>
										)}
									</Box>
								</Box>
								{jsonError && <Text color={'red'}>{jsonError}</Text>}
							</>
						)}
					</Flex>
					{/* <Flex maxH={"400px"} overflow={"auto"} sx={scrollBarStyle} className="file-preview" flexDir={"column"} p={4} w={"40%"}>
                        <CustomVariablesFile file={findCustomVariablesFiles()} />
                    </Flex> */}
					{/* <Flex w={"50%"}>
                        <Textarea h={"500px"}  value={formatJson(textData)} onChange={(e) => setTextData(e.target.value)}/>
                    </Flex> */}
					<Flex border={'1px solid black'} className={'variable-selector'} flex={1}>
						<Accordion allowToggle w={'100%'}>
							<AccordionItem>
								<AccordionButton w={'100%'}>Cores e fontes</AccordionButton>
								<AccordionPanel>
									<ColorsAndFontsSelector {...{ defaultVariables, setDefaultVariables }} />
								</AccordionPanel>
							</AccordionItem>
							<AccordionItem>
								<AccordionButton w={'100%'}>Variáveis</AccordionButton>
								<AccordionPanel>
									<VariablesGenerator {...{ variablesList, setVariablesList, defaultVariables }} />
								</AccordionPanel>
							</AccordionItem>
						</Accordion>
					</Flex>
				</Flex>
			</ChakraProvider>
			<div>
				<div className="canvas">
					<IntlProvider locale="br" defaultLocale="br">
						{compiledCss && <style>{compiledCss}</style>}
						{hefestoJson && <VtexJsonUploadComponent {...{ comp: hefestoJson }} />}
					</IntlProvider>
				</div>
			</div>
		</>
	);
};

const ColorsAndFontsSelector = ({ defaultVariables, setDefaultVariables }) => {
	const editableVars = [
		'--brand-color-primary-pure',
		'--brand-color-secondary-pure',
		'--cta-color-pure',
		'--support-color-1',
		'--support-color-2',
		'--support-color-3',
		'--support-color-4',
		'--feedback-color-success',
		'--feedback-color-warning',
		'--feedback-color-error',
	];

	if (!defaultVariables) return null;
	return (
		<Flex>
			<Box>
				<Text></Text>
				{editableVars.map((varName) => (
					<ChromePickerWrapper {...{ defaultVariables, setDefaultVariables, varName }} />
				))}
			</Box>
		</Flex>
	);
};

const ChromePickerWrapper = ({ defaultVariables, setDefaultVariables, varName }) => {
	const [color, setColor] = React.useState(defaultVariables[varName]);
	const [closeTimeout, setCloseTimeout] = React.useState(null);
	const [showColorPicker, setShowColorPicker] = React.useState(false);
	const debounceUpdateCss = useCallback(_.debounce(updateVariable, 1000), [varName]);

	React.useEffect(() => {
		setColor(defaultVariables[varName]);
	}, [varName]);

	function updateVariable() {
		setDefaultVariables({ ...defaultVariables, [varName]: color });
	}

	return (
		<Box>
			<Text>{varName}</Text>
			<Box bg={`var(${varName})`} w={'25px'} h={'25px'} borderRadius={'100%'} />
			{showColorPicker && <ChromePicker color={color} onChange={setColor} onChangeComplete={debounceUpdateCss} />}
		</Box>
	);
};

const JsonToHefestoHeader = ({ tryToCompile }) => {
	const [newIdentifierValue, setNewIdentifierValue] = React.useState('');
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [identifierList, setIdentifierList] = React.useState(['BrandGroup', 'ModalNewsletter']);

	function handleClick() {
		setIdentifierList([...identifierList, newIdentifierValue]);
		setNewIdentifierValue('');
		onClose();
	}
	function handleSaveChanges() {
		return () => {};
	}

	return (
		<Flex w={'100%'} sx={{ gap: '24px' }}>
			<Box>
				<Text>Upload de templates</Text>
				<Text>
					Dúvidas? Leia a documentação <a href={'#'}>aqui</a>
				</Text>
			</Box>
			<Box>
				<Text>Identificador do template</Text>
				<select>
					{identifierList.map((identifier) => (
						<option value={identifier}>{identifier}</option>
					))}
				</select>
				{
					<Popover onClose={onClose} isOpen={isOpen}>
						<PopoverTrigger>
							<IconButton size="sm" icon={<UilPlus />} onClick={onOpen} />
						</PopoverTrigger>
						<PopoverContent>
							<PopoverHeader>Nome do identificador</PopoverHeader>
							<PopoverArrow />
							<PopoverCloseButton />
							<Input
								onChange={(e) => setNewIdentifierValue(e.target.value)}
								placeholder="Digite o nome do identificador"
							/>
							<Button colorScheme={'yellow'} onClick={(e) => handleClick()}>
								Salvar
							</Button>
						</PopoverContent>
					</Popover>
				}
			</Box>
			<Box>
				<Button colorScheme={'yellow'} onClick={(e) => handleSaveChanges()}>
					Salvar alterações
				</Button>
			</Box>
			<Box>
				<Button onClick={(e) => tryToCompile()}>Compilar</Button>
			</Box>
		</Flex>
	);
};

const JsonStyle = {
	propertyStyle: { color: '#9CDCFE' },
	colonStyle: { color: 'white' },
	braceStyle: { color: 'white' },
	commaStyle: { color: 'white' },
	bracketStyle: { color: 'white' },
	booleanTrueStyle: { color: '#4e94ce' },
	booleanFalseStyle: { color: '#4e94ce' },
	nullStyle: { color: '#4e94ce' },
	stringStyle: { color: '#CE9178' },
	numberStyle: { color: '#B5CEA8' },
};

// const CustomVariablesFile = ({ file }) => {
//     if (!file) return null
//     return (
//         <>
//             <Text>{file.name}</Text>
//             <Box maxH={"400px"}  >
//                 <Box my={4} overflow={"auto"} fontSize={'sm'} sx={scrollBarStyle} bg={"#1e1e1e"} p={3} fontFamily={'Consolas'}>
//                     <JsonFormatter json={formatJson(file.content)} JsonStyle={JsonStyle} w={"100%"} color={"white"} p={3} overflow={'auto'} sx={scrollBarStyle} />
//                 </Box>
//             </Box>
//         </>
//     )
// }

const VariablesGenerator = ({ variablesList, setVariablesList, defaultVariables }) => {
	const typeList = Object.keys(types);

	function updateProperty(prop, value, index) {
		const newVariablesList = [...variablesList];
		const variableToBeUpdated = newVariablesList[index];
		variableToBeUpdated[prop] = value;
		setVariablesList(newVariablesList);
	}
	function removeVariable(index) {
		const newVariablesList = [...variablesList];
		newVariablesList.splice(index, 1);
		setVariablesList(newVariablesList);
	}
	function copyValueToClipBoard(index) {
		const variable = variablesList[index];
		navigator.clipboard.writeText(`var(--${variable.name})`);
	}
	return (
		<Box>
			<Flex>
				<Text>Variáveis</Text>
				<IconButton
					size="xs"
					icon={<UilPlus />}
					ml={4}
					onClick={(e) =>
						setVariablesList([...variablesList, { type: 'color', name: '', value: '--brand-color-primary-pure' }])
					}
				/>
			</Flex>
			<Flex flexDir="column" maxHeight={'280px'} overflow={'auto'} sx={scrollBarStyle}>
				{variablesList.map((variable, index) => (
					<Flex sx={{ gap: '8px' }} flexWrap={'wrap'} p={2} fontSize={'xs'} border={'1px solid #ccc'} borderRadius={8}>
						<Box width={'100%'}>
							<Text>Nome</Text>
							<Flex>
								<input
									style={{ width: '100%', border: '1px solid black' }}
									value={variable?.name}
									onChange={(e) => updateProperty('name', e.target.value, index)}
								/>
								<IconButton icon={<UilClipboard />} size={'xs'} onClick={() => copyValueToClipBoard(index)} />
							</Flex>
						</Box>
						<Box>
							<Text>Tipo</Text>
							<select defaultValue={typeList?.[0]} onChange={(e) => updateProperty('type', e.target.value, index)}>
								{typeList?.map((type) => (
									<option value={type}>{type}</option>
								))}
							</select>
						</Box>
						<Flex flexDir={'column'} flex={1}>
							<Text>Valor</Text>
							<select
								defaultValue={types[variable.type]?.[0]?.value}
								onChange={(e) => updateProperty('value', e.target.value, index)}
							>
								{types[variable.type].map((type) => (
									<option value={type.value}>{type.name}</option>
								))}
							</select>
							<span>{defaultVariables[variable.value]}</span>
						</Flex>
						<Box alignSelf={'center'}>
							<IconButton size="xs" icon={<UilTrashAlt />} ml={'auto'} onClick={(e) => removeVariable(index)} />
						</Box>
					</Flex>
				))}
			</Flex>
		</Box>
	);
};

export default VtexJsonToHefesto;
